import { useLocation, useNavigate } from 'react-router-dom';
import { EditorTypesIconsSet, SVG_SIZE_M } from 'src/constants';
import { useCreateEditorThread, useGetUser, useQueryParams } from 'src/hooks';
import { AppRoutes, ArtifactType } from 'src/types';
import { BetaLabel } from 'src/v2/commonComponents/BetaLabel';
import { Button } from 'src/v2/commonComponents/Button';
import { SpinnerSimple } from 'src/v2/commonComponents/SpinnerSimple';
import styles from './NewEditorButton.module.scss';

export type ThumbsUpFeedback = {
  feedback_type?: 'thumbs-up-feedback';
};
interface NewEditorButtonProps {
  buttonType: ArtifactType;
  withTitle?: boolean;
  withTooltip?: boolean;
  onClick?: () => void;
}

export const NewEditorButton = ({
  buttonType,
  withTitle,
  withTooltip,
  onClick,
}: NewEditorButtonProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { isOpenTier } = useGetUser();
  const { setSearchParams } = useQueryParams();

  const { onCreateEditorThread, isCreateEditorThreadLoading } =
    useCreateEditorThread();

  const handleClick = async () => {
    const result = await onCreateEditorThread({
      artifactType: buttonType,
    });

    if (!result?.thread?.thread_id) {
      return;
    }

    const { thread_id } = result.thread;

    if (location.pathname !== AppRoutes.HOME) {
      navigate(`${AppRoutes.HOME}?editor=${thread_id}`);
    } else {
      setSearchParams({ editor: thread_id }, { replace: true });
    }

    onClick?.();
  };

  const CurrentIcon = EditorTypesIconsSet[buttonType];
  const buttonTitle =
    buttonType === ArtifactType.CODE ? 'New Code' : 'New Document';

  const disabled = isCreateEditorThreadLoading || isOpenTier;

  const tooltipText = isOpenTier
    ? 'Available with subscription'
    : buttonType === ArtifactType.CODE
    ? 'Create new Code document'
    : 'Create new Editor document';

  return (
    <Button
      disabled={disabled}
      tooltipContent={withTooltip ? tooltipText : undefined}
      tooltipPlace={withTooltip ? 'bottom' : undefined}
      color="transparent"
      onClick={handleClick}
      shape={withTitle ? 'regular' : 'round'}
      fullWidthWithLeftAlign={withTitle}
    >
      {isCreateEditorThreadLoading ? (
        <SpinnerSimple size={SVG_SIZE_M} />
      ) : (
        <CurrentIcon size={SVG_SIZE_M} />
      )}
      {withTitle && buttonTitle}

      {!withTitle && <BetaLabel isAbsolute className={styles.label} />}
    </Button>
  );
};
